import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DevicesService, NotificationService } from '@nrwl/common-lib';
import { CollectorApiService } from '@nrwl/test-setup';
import { DataSourceService } from '../../../service/datatsource.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface AlarmSection {
  fieldName: string;
  alarmTitle: string;
  measurement?: string;
  measurementUnit?: string;
}

// interface ProbeAlarms {
//   [key: string]: {
//     status: string;
//     time: string | null;
//     alarmTitle?: string;
//     alarmMeasurement?: string;
//   };
// }

// interface InterfaceAlarms {
//   [key: string]: {
//     eqInterface: any | null;
//     nwInterface: any | null;
//     alarmTitle: string;
//     alarmMeasurement: string;
//   };
// }

@Component({
  selector: 'nrwl-alarms-dialog',
  templateUrl: './alarms-dialog.component.html',
  styleUrls: ['./alarms-dialog.component.scss']
})
export class AlarmsDialogComponent implements OnInit {
  loadingInProgress = false;
  objectKeys = Object.keys;
  jmepName = '';
  jmepAlarmsData = [];
  diagnostics = [];
  isLoading = false;
  probeAlarms = {};
  interfaceDisplayedColumns = ['alarmTitle', 'alarmMeasurement', 'eqInterface', 'nwInterface'];
  probeDisplayedColumns = ['alarmTitle', 'alarmMeasurement', 'status'];
  statusColors = {
    critical: '#d50032',
    major: '#e65e0c',
    minor: '#ffb500',
    default: '#9e9e9e' // For unknown or other statuses
  };

  interfaceAlarmsData = [];
  probeAlarmsData = [];
  interfaceAlarms = {};
  alarmSections: { [key: string]: AlarmSection } = {
    sfpFail: {
      fieldName: 'SFPFail',
      alarmTitle: 'SFP fail'
    },
    sfpComm: {
      fieldName: 'SFPComm',
      alarmTitle: 'SFP communications fail'
    },
    tempOOR: {
      fieldName: 'TempOOR',
      alarmTitle: 'Temperature out of range',
      measurement: 'temperature',
      measurementUnit: ' C'
    },
    voltageOOR: {
      fieldName: 'VoltageOOR',
      alarmTitle: 'Voltage out of range',
      measurement: 'vcc',
      measurementUnit: ' V'
    },
    laserFail: {
      fieldName: 'LaserFail',
      alarmTitle: 'Laser fail'
    },
    factory: {
      fieldName: 'FACTORY',
      alarmTitle: 'Factory load running'
    },
    swBankMismatch: {
      fieldName: 'SWBankMismatch',
      alarmTitle: 'Software bank mismatch'
    },
    incompatible: {
      fieldName: 'Incompatible',
      alarmTitle: 'Version incompatible with ESAM'
    },
    clockingMismatch: {
      fieldName: 'ClockingMismatch',
      alarmTitle: 'Time sync'
    },
    dyingGasp: {
      fieldName: 'DyingGasp',
      alarmTitle: 'Dying gasp'
    },
    los: {
      fieldName: 'LOS',
      alarmTitle: 'Loss of signal'
    },
    rxPowerLow: {
      fieldName: 'RxPwrLow',
      alarmTitle: 'Rx power too low',
      measurement: 'rx_power',
      measurementUnit: ' dBm'
    },
    rxOverload: {
      fieldName: 'RxOVLD',
      alarmTitle: 'Receiver overload'
    },
    txOutofrange: {
      fieldName: 'TxOOR',
      alarmTitle: 'Tx power out of range',
      measurement: 'tx_power',
      measurementUnit: ' dBm'
    },
    txDiable: {
      fieldName: 'TxDisable',
      alarmTitle: 'Tx disable'
    },
    lossOfLock: {
      fieldName: 'LOL',
      alarmTitle: 'Loss of lock'
    },
    linkDown: {
      fieldName: 'LNKDWN',
      alarmTitle: 'Link down'
    },
    loopback: {
      fieldName: 'Loopback',
      alarmTitle: 'Port loopback/RFC-6349 responder in progress'
    },
    evcLoopback: {
      fieldName: 'EVCLoopback',
      alarmTitle: 'Source specific loopback/RFC-6349 responder in progress'
    }
  };

  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private dialogRef: MatDialogRef<AlarmsDialogComponent>,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    @Inject(MAT_DIALOG_DATA) public data: any,
    private deviceService: DevicesService,
    private readonly dataSourceService: DataSourceService,
    private readonly notificationService: NotificationService,
    private collectorApiService: CollectorApiService
  ) {}

  ngOnInit(): void {
    this.getAlarmsConfig();
  }

  getAlarmsConfig(): void {
    this.isLoading = true;
    this.jmepAlarmsData = [];
    this.diagnostics = [];
    this.jmepName = this.data.dataItem.capabilities.system['hostname'];
    const esamUrl =
      this.data && this.data.dataItem[0]
        ? this.data.dataItem[0].connections[0].uri
        : this.data.dataItem.connections[0].uri;
    this.dataSourceService
      .jmepAlarms(esamUrl, this.data.dataItem.capabilities.phy['jmep-id'])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          if (
            response.status === 'success' &&
            response.data &&
            response.data.value &&
            response.data.value.data &&
            response.data.value.data.length
          ) {
            this.jmepAlarmsData = response.data.value.data;
            this.initAlarmDetails();
          }
        },
        () => {
          this.isLoading = false;
        }
      );

    const reqData = {
      name: this.data.dataItem.capabilities.system['hostname'],
      id: this.data.dataItem.capabilities.phy['jmep-id']
    };
    this.dataSourceService
      .jmepConfig(reqData)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          if (
            response.status === 'success' &&
            response.data &&
            response.data.value &&
            response.data.value.data &&
            response.data.value.data.length
          ) {
            const section = response.data.value.data.find(item => item.name === 'diagnostics');
            if (section && section.subAttributes && section.subAttributes.length) {
              this.diagnostics = section.subAttributes;
            }
            if (this.diagnostics && this.diagnostics.length) {
              this.initAlarmDetails();
            }
          }
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  initAlarmDetails(): void {
    if (this.jmepAlarmsData.length) {
      this.probeAlarms['sfpFail'] = this.getProbeAlarmsBySection(this.alarmSections['sfpFail']);
      this.probeAlarms['sfpComm'] = this.getProbeAlarmsBySection(this.alarmSections['sfpComm']);
      this.probeAlarms['tempOOR'] = this.getProbeAlarmsBySection(this.alarmSections['tempOOR']);
      this.probeAlarms['voltageOOR'] = this.getProbeAlarmsBySection(
        this.alarmSections['voltageOOR']
      );
      this.probeAlarms['laserFail'] = this.getProbeAlarmsBySection(this.alarmSections['laserFail']);
      this.probeAlarms['incompatible'] = this.getProbeAlarmsBySection(
        this.alarmSections['incompatible']
      );
      this.probeAlarms['dyingGasp'] = this.getProbeAlarmsBySection(this.alarmSections['dyingGasp']);

      this.interfaceAlarms['los'] = this.getInterfaceAlarms(this.alarmSections['los'], false);
      this.interfaceAlarms['rxPowerLow'] = this.getInterfaceAlarms(
        this.alarmSections['rxPowerLow'],
        false
      );
      this.interfaceAlarms['rxOverload'] = this.getInterfaceAlarms(
        this.alarmSections['rxOverload'],
        false
      );
      this.interfaceAlarms['txOutofrange'] = this.getInterfaceAlarms(
        this.alarmSections['txOutofrange'],
        false
      );
      this.interfaceAlarms['txDiable'] = this.getInterfaceAlarms(
        this.alarmSections['txDiable'],
        false
      );
      this.interfaceAlarms['lossOfLock'] = this.getInterfaceAlarms(
        this.alarmSections['lossOfLock'],
        true
      );
      this.interfaceAlarms['linkDown'] = this.getInterfaceAlarms(
        this.alarmSections['linkDown'],
        true
      );
      this.interfaceAlarms['loopback'] = this.getInterfaceAlarms(
        this.alarmSections['loopback'],
        true
      );
      this.interfaceAlarms['evcLoopback'] = this.getInterfaceAlarms(
        this.alarmSections['evcLoopback'],
        true
      );
    }

    this.interfaceAlarmsData = Object.keys(this.interfaceAlarms).map(key => {
      const alarm = this.interfaceAlarms[key];
      const alarmSection = this.alarmSections[key];
      const alarmTitle = alarmSection ? alarmSection.alarmTitle : 'Unknown Alarm';

      // Check for measurement and other dynamic properties based on the field
      const measurement = alarm && alarm.alarmMeasurement ? alarm.alarmMeasurement || '' : '';

      // Calculate the color based on the status
      // Check if status is 'cleared' and set it to '-'
      const eqInterfaceStatus =
        alarm.eqInterface?.status === 'cleared' ? '-' : alarm.eqInterface?.status || '';
      const nwInterfaceStatus =
        alarm.nwInterface?.status === 'cleared' ? '-' : alarm.nwInterface?.status || '';
      const eqStatusColor =
        this.statusColors[eqInterfaceStatus.toLowerCase()] || this.statusColors.default;
      const nwStatusColor =
        this.statusColors[nwInterfaceStatus.toLowerCase()] || this.statusColors.default;
      const time = alarm.nwInterface?.time || alarm.eqInterface?.time;

      return {
        alarmTitle,
        alarmMeasurement: measurement,
        eqInterfaceStatus,
        nwInterfaceStatus,
        eqStatusColor,
        nwStatusColor,
        time
      };
    });

    // Process Probe Alarms
    this.probeAlarmsData = Object.keys(this.probeAlarms).map(key => {
      const alarm = this.probeAlarms[key];
      const alarmSection = this.alarmSections[key];
      const alarmTitle = alarmSection ? alarmSection.alarmTitle : 'Unknown Alarm';

      // Check for measurement and other dynamic properties based on the field
      const measurement = alarm && alarm.alarmMeasurement ? alarm.alarmMeasurement || '' : '';

      // Calculate the color based on the status
      // Check if status is 'cleared' and set it to '-'
      const status = alarm.status === 'cleared' ? '-' : alarm.status || '';
      const statusColor = this.statusColors[status.toLowerCase()] || this.statusColors.default;
      const time = alarm?.time;

      return {
        alarmTitle,
        alarmMeasurement: measurement,
        status,
        statusColor,
        time
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getProbeAlarmsBySection(alarmSection: AlarmSection): any {
    const alarmData = this.findByValue(this.jmepAlarmsData, 'value', alarmSection.fieldName);
    const alarmObject = this.populateAlarmObject(alarmData);
    alarmObject.alarmTitle = alarmSection.alarmTitle;

    if (alarmSection.measurement) {
      this.populateAlarmMeasurement(alarmSection, alarmObject);
    }

    return alarmObject;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getInterfaceAlarms(alarmSection: AlarmSection, hasEqInterface: boolean): any {
    const responseObject = {
      eqInterface: null,
      nwInterface: null,
      alarmTitle: alarmSection.alarmTitle,
      alarmMeasurement: ''
    };

    const nwInterfaceAlarm = this.findInterfaceAlarms(
      alarmSection.fieldName,
      'alarmEntity',
      'network'
    );
    responseObject.nwInterface = this.populateAlarmObject(nwInterfaceAlarm);

    if (hasEqInterface) {
      const eqInterfaceAlarm = this.findInterfaceAlarms(
        alarmSection.fieldName,
        'alarmEntity',
        'equipment'
      );
      responseObject.eqInterface = this.populateAlarmObject(eqInterfaceAlarm);
    }

    this.populateAlarmMeasurement(alarmSection, responseObject);

    return responseObject;
  }

  findInterfaceAlarms(sectionName: string, entityId: string, entityValue: string): any {
    return this.jmepAlarmsData.find(data => {
      return (
        data.value === sectionName &&
        data.subAttributes.some(attr => attr.id === entityId && attr.value.includes(entityValue))
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  populateAlarmMeasurement(alarmSection: any, alarmObj: any): void {
    if (alarmSection.measurement && this.diagnostics.length) {
      const mv = this.diagnostics.find(item => item.id === alarmSection.measurement);
      alarmObj.alarmMeasurement = mv ? `${mv.value} ${alarmSection.measurementUnit}` : '';
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  populateAlarmObject(alarmData: any): any {
    const responseObject = {
      status: 'cleared',
      time: null
    };

    if (alarmData && alarmData.subAttributes) {
      const alarmState = this.findByValue(alarmData.subAttributes, 'id', 'alarmState');
      if (alarmState) {
        if (alarmState.value.toLowerCase() === 'cleared') {
          responseObject.status = 'cleared';
        } else {
          const severity = this.findByValue(alarmData.subAttributes, 'id', 'alarmSeverity');
          const alarmTime = this.findByValue(alarmData.subAttributes, 'id', 'alarmTime');

          responseObject.status = severity?.value || 'unknown';
          responseObject.time = alarmTime?.value || null;
        }
      }
    }

    return responseObject;
  }

  findByValue(data: any[], key: string, value: string): any {
    return data.find(item => item[key] === value);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  showTooltip(alarmObject: any): any {
    return alarmObject && alarmObject.status !== 'cleared';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getAlarmTooltip(alarmObject: any): any {
    const statusValue = alarmObject?.status;
    if (alarmObject && statusValue !== '-' && statusValue !== undefined) {
      return statusValue + ': ' + alarmObject.time;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getEqAlarmTooltip(alarmObject: any): any {
    const statusValue = alarmObject?.eqInterfaceStatus
      ? alarmObject?.eqInterfaceStatus
      : alarmObject?.status;
    if (alarmObject && statusValue !== '-' && statusValue !== undefined) {
      return statusValue + ': ' + alarmObject.time;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getNwAlarmTooltip(alarmObject: any): any {
    const statusValue = alarmObject?.nwInterfaceStatus
      ? alarmObject?.nwInterfaceStatus
      : alarmObject?.status;
    if (alarmObject && statusValue !== '-' && statusValue !== undefined) {
      return statusValue + ': ' + alarmObject.time;
    }
    return null;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  refreshAlarm(): void {
    this.getAlarmsConfig();
  }
}
